import {
  Box,
  Link,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Portal,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
  VStack,
} from '@chakra-ui/react';
import { useMemo } from 'react';
import moment from 'moment';
import { useEntityEdrContext } from '../../../context';
import { MISSING_DATA_PLACEHOLDER } from '@investigator/shared/constants';

export interface IdentityAlert {
  incident_id: string;
  type: string;
  severity: string;
  entities: string;
  timeline: string;
}

interface IdentityAlertsPopoverProps {
  alertCount: number;
  userId?: string;
}

// column configuration
const COLUMNS = [
  { key: 'incident_id', label: 'Incident ID', width: '103px' },
  { key: 'type', label: 'Type', width: '123px' },
  { key: 'severity', label: 'Severity', width: '110px' },
  { key: 'entities', label: 'Entities', width: '110px' },
  { key: 'timeline', label: 'Timeline', width: '110px' },
] as const;

export const IdentityAlertsPopover = ({
  alertCount,
  userId,
}: IdentityAlertsPopoverProps): JSX.Element => {
  const { identityAlerts } = useEntityEdrContext();

  const alerts = useMemo<IdentityAlert[]>(() => {
    const data = userId
      ? identityAlerts.data?.filter((item) => item.userId === userId)
      : identityAlerts.data;

    return (
      data?.map((item) => {
        const { incident } = item;
        const timestamp = incident?.timeline?.at(-1)?.timestamp;

        return {
          ...incident,
          entities:
            incident?.compromised_entities
              ?.map((e) => e?.primary_display_name)
              .filter(Boolean)
              .join(', ') || '-',
          timeline: timestamp
            ? moment.unix(timestamp).format('MMM D, YYYY h:mma')
            : '-',
        } as IdentityAlert;
      }) || []
    );
  }, [identityAlerts.data, userId]);

  return (
    <Popover trigger="hover" placement="bottom-start" isLazy gutter={12}>
      <PopoverTrigger>
        <Link
          color="blue.400"
          fontWeight="medium"
          cursor="pointer"
          _hover={{ color: 'blue.300' }}
        >
          {alertCount}
        </Link>
      </PopoverTrigger>
      <Portal>
        <PopoverContent
          w="588px"
          bg="layer.3"
          borderWidth="1px"
          borderColor="border.1"
          borderRadius="md"
          boxShadow="2xl"
          _focus={{ boxShadow: 'none', outline: 'none' }}
        >
          <PopoverArrow bg="layer.3" boxShadow="none" borderColor="border.1" />
          <PopoverBody p={0}>
            <VStack alignItems="start" spacing={0} w="100%">
              <Text
                fontWeight="semibold"
                textStyle="body-sm"
                color="text.primary"
                pt={5}
                pb={2}
                px={4}
              >
                Identity Alerts
              </Text>
              <Box px={4} pb={4} w="100%">
                <Table
                  variant="unstyled"
                  size="sm"
                  style={{ tableLayout: 'fixed' }}
                >
                  <Thead>
                    <Tr>
                      {COLUMNS.map((column) => (
                        <Th
                          key={column.key}
                          w={column.width}
                          color="text.primary"
                          textTransform="none"
                          fontWeight="semibold"
                          borderBottomWidth="1px"
                          borderColor="gray.600"
                          py={3}
                          pr={4}
                          pl={0}
                          textStyle="body-sm"
                        >
                          {column.label}
                        </Th>
                      ))}
                    </Tr>
                  </Thead>
                  <Tbody>
                    {alerts.map((alert, index) => (
                      <Tr key={index}>
                        {COLUMNS.map((column) => (
                          <Td
                            key={column.key}
                            borderBottomWidth={
                              index === alerts.length - 1 ? 0 : '1px'
                            }
                            borderColor="gray.600"
                            py={3}
                            pr={4}
                            pl={0}
                            color="text.primary"
                            fontWeight="normal"
                            textStyle="body-sm"
                          >
                            <Tooltip
                              label={
                                alert[column.key as keyof IdentityAlert] || ''
                              }
                              isDisabled={
                                !['type', 'timeline', 'entities'].includes(
                                  column.key,
                                )
                              }
                              hasArrow
                              placement="top"
                            >
                              <Text
                                noOfLines={1}
                                title={
                                  alert[column.key as keyof IdentityAlert] ||
                                  undefined
                                }
                                w="100%"
                              >
                                {alert[column.key as keyof IdentityAlert] ||
                                  MISSING_DATA_PLACEHOLDER}
                              </Text>
                            </Tooltip>
                          </Td>
                        ))}
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </Box>
            </VStack>
          </PopoverBody>
        </PopoverContent>
      </Portal>
    </Popover>
  );
};
